import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, Trash, FilePdf, Eye, CloudArrowUp, 
  FileText, ChartBar, ShoppingCart, X, LockKey, EyeSlash, Gear
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full URL for documents
const getDocumentUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Numeric keypad component - defined outside to avoid re-renders
function PinKeypad({ onInput, onBackspace }) {
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'back'].map((key, idx) => (
        <button
          key={idx}
          onClick={() => {
            if (key === 'back') onBackspace();
            else if (key !== null) onInput(String(key));
          }}
          disabled={key === null}
          className={`
            h-16 rounded-xl text-2xl font-semibold transition-all
            ${key === null ? 'invisible' : ''}
            ${key === 'back' 
              ? 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-300' 
              : 'bg-white dark:bg-stone-800 text-stone-900 dark:text-white hover:bg-[#00A19A]/20 active:bg-[#00A19A]/30 shadow-sm border border-stone-200 dark:border-stone-700'
            }
          `}
          data-testid={`pin-keypad-${key}`}
        >
          {key === 'back' ? '⌫' : key}
        </button>
      ))}
    </div>
  );
}

export default function PrivateSection() {
  const { settings, requiresPin, verifyPin, verifyBiometric, logout, t } = useSettings();
  
  const [activeTab, setActiveTab] = useState("precos");
  const [documents, setDocuments] = useState({
    precos: [],
    relatorios: [],
    vendas: []
  });
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "" });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: null });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // PIN authentication state
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [biometricAttempted, setBiometricAttempted] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Try biometric auth on mount if enabled
  useEffect(() => {
    if (settings.biometricEnabled && requiresPin() && !biometricAttempted) {
      setBiometricAttempted(true);
      handleBiometricAuth();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.biometricEnabled]);

  const handleBiometricAuth = async () => {
    setBiometricLoading(true);
    try {
      const success = await verifyBiometric();
      if (!success) {
        // Biometric failed, user can try PIN
        setPinError('');
      }
    } catch (error) {
      console.error('Biometric auth error:', error);
    } finally {
      setBiometricLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/private-documents`);
      // Group by category
      const grouped = {
        precos: response.data.filter(d => d.category === 'precos'),
        relatorios: response.data.filter(d => d.category === 'relatorios'),
        vendas: response.data.filter(d => d.category === 'vendas')
      };
      setDocuments(grouped);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // PIN handlers
  const handlePinInput = useCallback((digit) => {
    setPinValue(prev => {
      if (prev.length >= 4) return prev;
      const newPin = prev + digit;
      
      // Auto-submit when 4 digits entered
      if (newPin.length === 4 && requiresPin()) {
        // Defer verification to avoid state updates during setState
        setTimeout(() => {
          const isValid = verifyPin(newPin);
          if (!isValid) {
            setPinError(t('private_pin_error') || 'PIN incorreto');
            setPinValue('');
          }
        }, 50);
      }
      
      return newPin;
    });
    setPinError('');
  }, [requiresPin, verifyPin, t]);

  const handlePinBackspace = useCallback(() => {
    setPinValue(prev => prev.slice(0, -1));
    setPinError('');
  }, []);

  const handlePinSubmit = useCallback(() => {
    if (pinValue.length !== 4) {
      setPinError(t('settings_pin_error_4digits') || 'O PIN deve ter 4 dígitos');
      return;
    }
    const isValid = verifyPin(pinValue);
    if (!isValid) {
      setPinError(t('private_pin_error') || 'PIN incorreto');
      setPinValue('');
    }
  }, [pinValue, verifyPin, t]);

  const openUploadDialog = (category) => {
    setUploadCategory(category);
    setUploadForm({ name: "" });
    setSelectedFile(null);
    setShowUploadDialog(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadForm.name) {
        // Auto-fill name from filename
        setUploadForm({ name: file.name.replace('.pdf', '') });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.name) {
      toast.error("Selecione um arquivo e preencha o nome");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', uploadForm.name);
      formData.append('category', uploadCategory);

      await axios.post(`${API}/private-documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });

      toast.success("Documento carregado com sucesso");
      setShowUploadDialog(false);
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erro ao carregar documento");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const confirmDelete = (id, name) => {
    setDeleteConfirm({ open: true, id, name });
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/private-documents/${deleteConfirm.id}`);
      toast.success("Documento eliminado");
      setDeleteConfirm({ open: false, id: null, name: null });
      fetchDocuments();
    } catch (error) {
      toast.error("Erro ao eliminar documento");
    }
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'precos': return 'Tabela de Preços';
      case 'relatorios': return 'Relatórios';
      case 'vendas': return 'Vendas';
      default: return category;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'precos': return <FileText size={24} weight="duotone" />;
      case 'relatorios': return <ChartBar size={24} weight="duotone" />;
      case 'vendas': return <ShoppingCart size={24} weight="duotone" />;
      default: return <FilePdf size={24} />;
    }
  };

  const renderDocumentsList = (category) => {
    const docs = documents[category] || [];
    
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00A19A]/10 rounded-lg text-[#00A19A]">
              {getCategoryIcon(category)}
            </div>
            <h2 className="text-lg sm:text-xl font-semibold font-[Manrope] text-stone-900">
              {getCategoryTitle(category)}
            </h2>
          </div>
          <Button 
            className="btn-primary w-full sm:w-auto"
            onClick={() => openUploadDialog(category)}
          >
            <Plus size={18} />
            Adicionar
          </Button>
        </div>

        {docs.length === 0 ? (
          <div className="empty-state py-12 bg-stone-50 rounded-lg">
            <FilePdf size={48} className="text-stone-300 mb-3" />
            <p className="text-stone-500">Sem documentos nesta secção</p>
            <p className="text-sm text-stone-400 mt-1">Clique em &quot;Adicionar&quot; para carregar um PDF</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((doc) => (
              <div 
                key={doc.id} 
                className="gresart-card group hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-red-50 rounded-lg text-red-500 flex-shrink-0">
                    <FilePdf size={28} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-stone-900 truncate">{doc.name}</h3>
                    <p className="text-xs text-stone-400 mt-1">
                      {new Date(doc.uploaded_at).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
                  <a
                    href={getDocumentUrl(doc.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 text-sm py-2 px-3 bg-[#00A19A] text-white rounded-lg hover:bg-[#008B85] transition-colors font-medium"
                  >
                    <Eye size={16} />
                    Ver
                  </a>
                  <button
                    onClick={() => confirmDelete(doc.id, doc.name)}
                    className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  // PIN authentication screen
  if (requiresPin()) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="pin-lock-screen">
        <div className="w-full max-w-md px-6">
          {/* Lock Icon */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-[#00A19A]/10 rounded-full flex items-center justify-center mb-4">
              <LockKey size={40} weight="duotone" className="text-[#00A19A]" />
            </div>
            <h1 className="text-2xl font-bold font-[Manrope] text-stone-900 dark:text-white">
              {t('private_title') || 'Secção Privada'}
            </h1>
            <p className="text-stone-500 dark:text-stone-400 mt-2">
              {settings.biometricEnabled 
                ? (t('private_use_biometric_or_pin') || 'Use Face ID / Touch ID ou introduza o PIN')
                : (t('private_enter_pin') || 'Introduza o PIN')}
            </p>
          </div>

          {/* Face ID / Touch ID Button - if enabled */}
          {settings.biometricEnabled && (
            <div className="mb-8">
              <Button
                onClick={handleBiometricAuth}
                disabled={biometricLoading}
                className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700 text-white rounded-xl flex items-center justify-center gap-3"
                data-testid="btn-biometric-unlock"
              >
                {biometricLoading ? (
                  <LoadingSpinner size={24} />
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a5 5 0 0 0-5 5v3a5 5 0 0 0 10 0V7a5 5 0 0 0-5-5z"/>
                      <path d="M12 14v4"/>
                      <path d="M8 18h8"/>
                      <circle cx="12" cy="8" r="1"/>
                    </svg>
                    {t('private_use_biometric') || 'Usar Face ID / Touch ID'}
                  </>
                )}
              </Button>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-stone-300 dark:border-stone-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#FAFAF9] dark:bg-stone-900 px-2 text-stone-500">
                    {t('private_or') || 'ou'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* PIN Display */}
          <div className="flex justify-center gap-4 mb-8">
            {[0, 1, 2, 3].map((i) => {
              const filled = pinValue.length > i;
              return (
                <div
                  key={i}
                  className={`w-16 h-16 rounded-xl border-2 flex items-center justify-center text-3xl font-bold transition-all ${
                    filled 
                      ? 'border-[#00A19A] bg-[#00A19A]/10 text-[#00A19A]' 
                      : 'border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800'
                  } ${pinError && 'border-red-400 animate-shake'}`}
                >
                  {filled ? (showPin ? pinValue[i] : '•') : ''}
                </div>
              );
            })}
          </div>

          {/* Show/Hide toggle */}
          <button
            onClick={() => setShowPin(!showPin)}
            className="mx-auto flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 dark:text-stone-400 mb-4"
          >
            {showPin ? <EyeSlash size={18} /> : <Eye size={18} />}
            {showPin ? 'Ocultar' : 'Mostrar'}
          </button>

          {/* Error message */}
          {pinError && (
            <p className="text-center text-red-500 text-sm font-medium mb-4">{pinError}</p>
          )}

          {/* Text input alternative */}
          <div className="text-center mb-6">
            <Input
              type={showPin ? 'text' : 'password'}
              maxLength={4}
              value={pinValue}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                setPinValue(val);
                setPinError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pinValue.length === 4) {
                  handlePinSubmit();
                }
              }}
              className="text-center text-2xl tracking-[0.5em] max-w-[180px] mx-auto"
              placeholder="••••"
              autoFocus={!settings.biometricEnabled}
              data-testid="pin-input"
            />
          </div>

          {/* Numeric Keypad */}
          <PinKeypad onInput={handlePinInput} onBackspace={handlePinBackspace} />

          {/* Unlock Button */}
          <Button
            className="w-full mt-6 btn-primary py-6 text-lg"
            disabled={pinValue.length !== 4}
            onClick={handlePinSubmit}
            data-testid="btn-unlock"
          >
            <LockKey size={20} className="mr-2" />
            {t('private_unlock') || 'Desbloquear'}
          </Button>

          {/* Back link */}
          <div className="text-center mt-6">
            <Link 
              to="/" 
              className="text-sm text-stone-500 hover:text-[#00A19A] transition-colors"
            >
              {t('common_back') || 'Voltar'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          icon={LockKey}
          title={t('private_title') || 'Secção Privada'}
          subtitle={t('private_subtitle') || 'Documentos confidenciais da empresa'}
        />
        <div className="flex items-center gap-2">
          <Link to="/settings">
            <Button variant="ghost" size="icon" className="text-stone-500 hover:text-[#00A19A]" data-testid="btn-settings">
              <Gear size={22} />
            </Button>
          </Link>
          {settings.pinEnabled && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={logout}
              className="text-stone-500 hover:text-red-500"
              data-testid="btn-logout"
            >
              <LockKey size={18} className="mr-1" />
              {t('private_lock') || 'Bloquear'}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 h-auto">
          <TabsTrigger value="precos" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 px-2">
            <FileText size={18} className="flex-shrink-0" />
            <span className="text-xs sm:text-sm text-center leading-tight">Tabela de Preços</span>
          </TabsTrigger>
          <TabsTrigger value="relatorios" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 px-2">
            <ChartBar size={18} className="flex-shrink-0" />
            <span className="text-xs sm:text-sm text-center leading-tight">Relatórios</span>
          </TabsTrigger>
          <TabsTrigger value="vendas" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 px-2">
            <ShoppingCart size={18} className="flex-shrink-0" />
            <span className="text-xs sm:text-sm text-center leading-tight">Vendas</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="precos">
          {renderDocumentsList('precos')}
        </TabsContent>

        <TabsContent value="relatorios">
          {renderDocumentsList('relatorios')}
        </TabsContent>

        <TabsContent value="vendas">
          {renderDocumentsList('vendas')}
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Adicionar Documento - {getCategoryTitle(uploadCategory)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="doc-name">Nome do Documento</Label>
              <Input
                id="doc-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ name: e.target.value })}
                placeholder="Ex: Lista de Preços 2025"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Arquivo PDF</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFile ? (
                <div className="mt-2 p-4 bg-stone-50 rounded-lg border border-stone-200 flex items-center gap-3">
                  <FilePdf size={32} className="text-red-500" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-stone-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-1 hover:bg-stone-200 rounded"
                  >
                    <X size={16} className="text-stone-500" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 w-full h-32 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#00A19A] hover:bg-teal-50/50 transition-colors"
                >
                  <CloudArrowUp size={32} className="text-stone-400" />
                  <span className="text-sm text-stone-500">Clique para selecionar PDF</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !uploadForm.name}
            >
              {uploading ? "A carregar..." : "Adicionar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, id: null, name: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] text-red-600 flex items-center gap-2">
              <Trash size={20} />
              Confirmar Eliminação
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-stone-600">Tem certeza que deseja eliminar este documento?</p>
            {deleteConfirm.name && (
              <p className="mt-2 font-medium text-stone-900 bg-stone-100 px-3 py-2 rounded">
                {deleteConfirm.name}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, id: null, name: null })}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
